import { Box, Chip, Stack } from "@mui/material";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { useMemo } from "react";

import { useTableSearchParams } from "Forms/Hooks/useTableSearchParams";
import {
  type InvoiceListItemDto,
  InvoiceStatus,
  InvoiceType,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useInvoiceListQuery } from "Invoices/Api/Queries/useInvoiceListQuery";
import { InvoiceDownloadIconButton } from "Invoices/Components/InvoiceDownloadIconButton";
import { TableSearchInput } from "Projects/Components/TableSearchInput";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import { BlDropdown, type BlDropdownValueType } from "Shared/Inputs/BlDropdown";
import { Price } from "Shared/Price/Price";
import { DateFormat, formatDate } from "Shared/Utils/Format/date";

const columnHelper = createColumnHelper<InvoiceListItemDto>();

interface InvoiceListTableProps {
  isAdmin?: boolean;
}

export const InvoiceListTable = ({
  isAdmin = false,
}: InvoiceListTableProps): React.ReactNode => {
  const { t } = useResource();

  const [invoiceTypeFilter, setInvoiceTypeFilter] = useQueryState(
    "type",
    parseAsStringEnum<InvoiceType>(Object.values(InvoiceType)),
  );

  const {
    search,
    debouncedSearch,
    onSearchChange,
    pageIndex,
    pageSize,
    onPaginationChange,
    resetPage,
  } = useTableSearchParams();

  const handleFilterChange = (value: BlDropdownValueType) => {
    setInvoiceTypeFilter(value === "" ? null : (value as InvoiceType));
    resetPage();
  };

  const { data, isLoading } = useInvoiceListQuery({
    offset: pageIndex * pageSize,
    limit: pageSize,
    invoiceType: invoiceTypeFilter,
    search: debouncedSearch || null,
    isAdmin,
  });

  const invoices = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  const invoiceTypeOptions = useMemo(
    () => [
      { value: "", label: t(Resources.Common.All) },
      {
        value: InvoiceType.Standard,
        label: t(Resources.Invoices.Types.Standard),
      },
      {
        value: InvoiceType.SelfBilling,
        label: t(Resources.Invoices.Types.SelfBilling),
      },
    ],
    [t],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("invoiceNumber", {
        header: t(Resources.Invoices.InvoiceNumber),
        enableSorting: false,
        cell: (info) => <Box sx={{ fontWeight: 600 }}>{info.getValue()}</Box>,
      }),
      columnHelper.accessor("issueDate", {
        header: t(Resources.Invoices.IssueDate),
        enableSorting: false,
        cell: (info) => (
          <Box>{formatDate(info.getValue(), DateFormat.Date)}</Box>
        ),
      }),
      columnHelper.accessor("invoiceType", {
        header: t(Resources.Invoices.Type),
        enableSorting: false,
        cell: (info) => {
          const type = info.getValue();
          return (
            <Chip
              label={t(
                Resources.Invoices.Types[
                  type as keyof typeof Resources.Invoices.Types
                ],
              )}
              size="small"
              color={type === InvoiceType.SelfBilling ? "info" : "default"}
              variant="outlined"
            />
          );
        },
      }),
      columnHelper.accessor("sellerName", {
        header: t(Resources.Invoices.Seller),
        enableSorting: false,
        meta: { fillRow: true },
      }),
      columnHelper.accessor("buyerName", {
        header: t(Resources.Invoices.Buyer),
        enableSorting: false,
        meta: { fillRow: true },
      }),
      columnHelper.accessor("totalGrossAmount", {
        header: t(Resources.Invoices.TotalAmount),
        enableSorting: false,
        cell: (info) => (
          <Box sx={{ fontWeight: 600 }}>
            <Price amount={info.getValue()} />
          </Box>
        ),
      }),
      columnHelper.accessor("status", {
        header: t(Resources.Invoices.Status),
        enableSorting: false,
        cell: (info) => {
          const status = info.getValue();
          return (
            <Chip
              label={t(
                Resources.Invoices.Statuses[
                  status as keyof typeof Resources.Invoices.Statuses
                ],
              )}
              size="small"
              color={
                status === InvoiceStatus.Paid
                  ? "success"
                  : status === InvoiceStatus.Cancelled
                    ? "error"
                    : "warning"
              }
            />
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        cell: (info) => (
          <InvoiceDownloadIconButton invoiceID={info.row.original.invoiceID} />
        ),
      }),
    ],
    [t],
  );

  const table = useReactTable({
    data: invoices,
    columns,
    getRowId: (row) => row.invoiceID,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: totalCount,
    state: { pagination: { pageIndex, pageSize } },
    onPaginationChange,
  });

  return (
    <Box>
      <Stack direction="row" spacing={Spacing.M} sx={{ mb: Spacing.M }}>
        <Box sx={{ flexGrow: 1 }}>
          <TableSearchInput
            name="invoice-search"
            value={search}
            onChange={onSearchChange}
            placeholder={t(Resources.Invoices.SearchPlaceholder)}
          />
        </Box>
        <Box sx={{ minWidth: 200 }}>
          <BlDropdown
            name="invoiceTypeFilter"
            options={invoiceTypeOptions}
            value={invoiceTypeFilter ?? ""}
            onChange={handleFilterChange}
            size="small"
          />
        </Box>
      </Stack>
      <BlTanstackTable
        table={table}
        isLoading={isLoading}
        totalCount={totalCount}
      />
    </Box>
  );
};
